import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Car, 
  Clock, 
  CheckCircle, 
  ArrowLeft,
  Sun,
  Moon,
  Activity
} from 'lucide-react';
import NotificationBell from '../components/NotificationBell';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TripAnalytics() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/analytics/trips`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to fetch analytics');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="p-6">
          <p className="text-muted-foreground">Failed to load analytics</p>
          <Button onClick={() => navigate(-1)} className="mt-4">Go Back</Button>
        </Card>
      </div>
    );
  }

  const { summary, sessions_by_day, driver_performance, weekly_trend } = analytics;

  // Calculate max for chart scaling
  const maxSessions = Math.max(...Object.values(sessions_by_day), 1);
  const maxWeekly = Math.max(...weekly_trend.map(w => w.sessions), 1);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="trip-analytics-page">
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-button">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-heading font-bold text-slate-800">Trip Analytics</h1>
                <p className="text-sm text-slate-500">Performance insights and statistics</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <NotificationBell />
              <Button variant="outline" onClick={logout} className="rounded-full">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          <Card className="shadow-lg" data-testid="total-trips-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Trips</p>
                  <p className="text-3xl font-bold text-blue-600">{summary.total_trips}</p>
                </div>
                <BarChart3 className="w-8 h-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg" data-testid="active-trips-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Trips</p>
                  <p className="text-3xl font-bold text-green-600">{summary.active_trips}</p>
                </div>
                <Activity className="w-8 h-8 text-green-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg" data-testid="completed-sessions-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Completed</p>
                  <p className="text-3xl font-bold text-purple-600">{summary.completed_sessions}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-purple-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg" data-testid="kids-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Kids</p>
                  <p className="text-3xl font-bold text-orange-600">{summary.active_kids}</p>
                </div>
                <Users className="w-8 h-8 text-orange-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg" data-testid="drivers-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Drivers</p>
                  <p className="text-3xl font-bold text-teal-600">{summary.active_drivers}</p>
                </div>
                <Car className="w-8 h-8 text-teal-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg" data-testid="ontime-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">On-Time Rate</p>
                  <p className="text-3xl font-bold text-emerald-600">{summary.on_time_rate}%</p>
                </div>
                <Clock className="w-8 h-8 text-emerald-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Trip Type Distribution */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card className="shadow-lg" data-testid="trip-types-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sun className="w-5 h-5 text-yellow-500" />
                Trip Types
              </CardTitle>
              <CardDescription>Morning vs Afternoon trips</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 w-32">
                    <Sun className="w-4 h-4 text-yellow-500" />
                    <span className="text-sm">Morning</span>
                  </div>
                  <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
                    <div 
                      className="bg-yellow-500 h-full rounded-full transition-all duration-500"
                      style={{ width: `${(summary.morning_trips / Math.max(summary.total_trips, 1)) * 100}%` }}
                    ></div>
                  </div>
                  <span className="w-12 text-right font-semibold">{summary.morning_trips}</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 w-32">
                    <Moon className="w-4 h-4 text-indigo-500" />
                    <span className="text-sm">Afternoon</span>
                  </div>
                  <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
                    <div 
                      className="bg-indigo-500 h-full rounded-full transition-all duration-500"
                      style={{ width: `${(summary.afternoon_trips / Math.max(summary.total_trips, 1)) * 100}%` }}
                    ></div>
                  </div>
                  <span className="w-12 text-right font-semibold">{summary.afternoon_trips}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg" data-testid="weekly-trend-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-500" />
                Weekly Trend
              </CardTitle>
              <CardDescription>Sessions over the last 4 weeks</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-end gap-2 h-32">
                {weekly_trend.map((week, index) => (
                  <div key={index} className="flex-1 flex flex-col items-center gap-2">
                    <div className="w-full bg-slate-100 rounded-t relative" style={{ height: '100px' }}>
                      <div 
                        className="absolute bottom-0 w-full bg-blue-500 rounded-t transition-all duration-500"
                        style={{ height: `${(week.sessions / maxWeekly) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-xs text-muted-foreground">{week.week}</span>
                    <span className="text-xs font-semibold">{week.sessions}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sessions by Day */}
        <Card className="shadow-lg mb-8" data-testid="sessions-by-day-card">
          <CardHeader>
            <CardTitle>Sessions by Day of Week</CardTitle>
            <CardDescription>Trip sessions distribution over the last 30 days</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-2 h-40">
              {Object.entries(sessions_by_day).map(([day, count]) => (
                <div key={day} className="flex-1 flex flex-col items-center gap-2">
                  <div className="w-full bg-slate-100 rounded-t relative" style={{ height: '120px' }}>
                    <div 
                      className="absolute bottom-0 w-full bg-gradient-to-t from-blue-600 to-blue-400 rounded-t transition-all duration-500"
                      style={{ height: `${(count / maxSessions) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-muted-foreground">{day.slice(0, 3)}</span>
                  <span className="text-sm font-semibold">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Driver Performance */}
        <Card className="shadow-lg" data-testid="driver-performance-card">
          <CardHeader>
            <CardTitle>Driver Performance</CardTitle>
            <CardDescription>Top performing drivers by completed sessions</CardDescription>
          </CardHeader>
          <CardContent>
            {driver_performance.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No driver data available</p>
            ) : (
              <div className="space-y-3">
                {driver_performance.map((driver, index) => (
                  <div 
                    key={driver.driver_id} 
                    className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    data-testid={`driver-row-${index}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{driver.driver_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {driver.total_trips} trips • {driver.kids_count} kids
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-blue-600">{driver.completed_sessions}</p>
                      <p className="text-xs text-muted-foreground">sessions</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
